import { useRef, useState } from 'react';
import { apiAddWidget, apiAdminAddTenantWidget } from './api';

const WIDGET_TYPES: { id: string; label: string; description: string }[] = [
  { id: 'kpi',      label: 'KPI',        description: 'Single number card' },
  { id: 'count',    label: 'Count',      description: 'Total record count' },
  { id: 'line',     label: 'Line',       description: 'Trend over time' },
  { id: 'area',     label: 'Area',       description: 'Filled trend chart' },
  { id: 'bar',      label: 'Bar',        description: 'Value by category' },
  { id: 'top_n',    label: 'Top N',      description: 'Ranked items' },
  { id: 'pie',      label: 'Pie',        description: 'Category breakdown' },
  { id: 'count_by', label: 'Count By',   description: 'Records by category' },
];

interface Props {
  onClose: () => void;
  onAdded: (widget: Record<string, unknown>) => void;
  scope?: 'user' | 'admin';
}

export default function AddWidgetDialog({ onClose, onAdded, scope = 'user' }: Props) {
  const [step, setStep]         = useState<1 | 2>(1);
  const [type, setType]         = useState('');
  const [prompt, setPrompt]     = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const textareaRef             = useRef<HTMLTextAreaElement>(null);

  const handleTypeSelect = (id: string) => {
    setType(id);
    setStep(2);
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  const handleSubmit = async () => {
    if (!prompt.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const addApi = scope === 'admin' ? apiAdminAddTenantWidget : apiAddWidget;
      const { widget } = await addApi(type, prompt.trim());
      onAdded(widget);
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-background border border-border rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h2 className="text-base font-semibold">Add Widget</h2>
            {step === 2 && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {WIDGET_TYPES.find(t => t.id === type)?.label} — describe what you want to see
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors text-lg"
          >×</button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {step === 1 && (
            <div className="grid grid-cols-4 gap-2">
              {WIDGET_TYPES.map(wt => (
                <button
                  key={wt.id}
                  onClick={() => handleTypeSelect(wt.id)}
                  className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-border hover:border-primary/40 hover:bg-primary/5 transition-all text-center group"
                >
                  <span className="text-sm font-medium group-hover:text-primary transition-colors">{wt.label}</span>
                  <span className="text-[10px] text-muted-foreground leading-tight">{wt.description}</span>
                </button>
              ))}
            </div>
          )}

          {step === 2 && (
            <div className="flex flex-col gap-4">
              <textarea
                ref={textareaRef}
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSubmit(); }}
                placeholder="e.g. total revenue by branch for the last 6 months"
                rows={4}
                disabled={loading}
                className="w-full rounded-xl border border-border bg-muted/30 px-4 py-3 text-sm placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-50"
              />
              {error && (
                <p className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">{error}</p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {step === 2 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-border bg-muted/20">
            <button
              onClick={() => { setStep(1); setError(null); }}
              disabled={loading}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
            >← Back</button>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                disabled={loading}
                className="px-4 py-1.5 rounded-lg border border-border text-sm hover:bg-accent transition-colors disabled:opacity-50"
              >Cancel</button>
              <button
                onClick={handleSubmit}
                disabled={loading || !prompt.trim()}
                className="px-4 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <span className="w-3.5 h-3.5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                    Generating…
                  </>
                ) : 'Generate Widget'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
